import {
  CalendarCheck2,
  CalendarClock,
  ChevronsRight,
  CircleAlert,
  Clock,
  Eye,
  Gauge,
  Locate,
  MessageSquare,
  MessageSquarePlus,
  Truck,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import {
  ACTIONS,
  Route2g,
  RouteStop,
  stopActionLabel,
} from "@/reducers/routes2gSlice"
import { Location } from "@/reducers/locationsSlice"
import {
  formatDuration,
  formatUserFriendlyDate,
} from "@/components/RouteStopView"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

export type DriverInfoProps = {
  jid: string
  name: string
  avatarName: string | undefined
  avatarPhoto?: string | undefined
  avatarColor: string

  plates?: string | undefined

  hasRoute?: boolean
  nextStop?: RouteStop

  distance?: string

  isDriving: boolean
  isLate: boolean
  isNextStopLate: boolean
  isTracked: boolean
  isOffRoute: boolean
  showAlert: boolean

  unreadCount?: number

  onNavigateToChat?: (jid: string, shouldJoin: boolean) => void
  onClose?: () => void
  onFlyToLocation?: () => void
  chatJid?: string | undefined
  stops?: Route2g["stops"]
  shouldJoin?: boolean
  /** The truck's current speed, e.g. "62 km/h". */
  speedLabel?: string
}

const getActionIcon = (stop: RouteStop, color: string = "") => {
  const action =
    ACTIONS.find((action) => action.value.toLowerCase() === stop.action) ||
    ACTIONS.find((action) => action.value === "other")!

  return <action.icon className={`h-4 w-4 ${color}`} size={12} />
}

const getActionLabel = (t: TFunction, stop: RouteStop) => {
  const action =
    ACTIONS.find((action) => action.value.toLowerCase() === stop.action) ||
    ACTIONS.find((action) => action.value === "other")!

  return stopActionLabel(t, action.value) ?? action.label
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const getCoordinates = (location: Location) => {
  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }
  return undefined
}

const getAddress = (stop: RouteStop) => {
  if (stop.location.formattedAddress) {
    return stop.location.formattedAddress
  } else if (stop.location.countryCode && stop.location.locality) {
    return `${stop.location.countryCode}, ${stop.location.postalCode ? stop.location.postalCode : ""} ${stop.location.locality}`
  }
  return ""
}

const getRemainingTimeWithBreaks = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time_with_tacho) {
    return undefined
  }
  return Math.max(
    Math.floor(
      new Date(routeStop.estimated_arrival_time_with_tacho).getTime() -
        new Date().getTime(),
    ) / 1000,
    0,
  )
}

const getRemainingTime = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time) {
    return 0
  }

  return Math.max(
    Math.floor(
      new Date(routeStop.estimated_arrival_time).getTime() -
        new Date().getTime(),
    ) / 1000,
    0,
  )
}

export default function DriverInfo({
  jid,
  name,
  avatarName,
  avatarPhoto,
  avatarColor,
  distance,
  unreadCount = 0,
  isDriving,
  isNextStopLate,
  hasRoute,
  isTracked,
  isOffRoute,
  showAlert,
  nextStop,
  chatJid,
  onClose,
  onNavigateToChat,
  onFlyToLocation,
  shouldJoin = false,
  speedLabel,
}: DriverInfoProps) {
  const { t } = useTranslation()
  let lateIndicatorColor = "accent-foreground"
  if (showAlert) {
    if (isNextStopLate) {
      lateIndicatorColor = "text-destructive"
    } else lateIndicatorColor = "text-green-700"
  }
  return (
    <>
      {/* Header */}
      <div className="border-base flex items-center justify-between border-b py-2 pl-3 pr-2">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="relative self-start">
            <Avatar
              className={cn(
                "h-6 w-6",
                isDriving && "border-2 border-green-600",
              )}
            >
              {avatarPhoto && <AvatarImage src={avatarPhoto} alt={name} />}
              <AvatarFallback
                style={{ backgroundColor: avatarColor }}
                className="text-xs font-medium text-gray-900"
              >
                {getInitials(avatarName ?? name)}
              </AvatarFallback>
            </Avatar>
            {isDriving && (
              <div className="absolute -bottom-1 right-0 flex h-3 w-3 items-center justify-center rounded-full bg-green-600">
                <ChevronsRight
                  className="text-white dark:text-primary-foreground"
                  size={10}
                />
              </div>
            )}
          </div>
          <div>
            <h2 className="max-w-[200px] truncate text-sm font-semibold text-accent-foreground">
              {name}
            </h2>
            {/* Always rendered: the map popup is static HTML and patches this
                node in place when a new position arrives. */}
            <div
              data-car-speed
              className={cn(
                "flex items-center gap-1 text-xs text-muted-foreground",
                !speedLabel && "hidden",
              )}
            >
              <Gauge className="h-3 w-3" />
              <span data-car-speed-label>{speedLabel}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onFlyToLocation && (
            <Button
              data-fly-jid={chatJid || jid}
              variant="outline"
              size="sm"
              className="relative h-8 w-8 p-0 hover:bg-sidebar-accent"
              onClick={onFlyToLocation}
            >
              <Locate className="h-4 w-4" />
            </Button>
          )}

          {chatJid && onNavigateToChat && (
            <Button
              data-chat-jid={chatJid || jid}
              variant="outline"
              size="sm"
              className="relative h-8 w-8 p-0 hover:bg-sidebar-accent"
              onClick={() => onNavigateToChat?.(chatJid, shouldJoin)}
            >
              {shouldJoin && <MessageSquarePlus className="h-4 w-4" />}
              {!shouldJoin && <MessageSquare className="h-4 w-4" />}
              {!shouldJoin && unreadCount > 0 && (
                <div className="absolute -right-1/4 -top-1/4 flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 font-semibold text-white">
                  {unreadCount}
                </div>
              )}
            </Button>
          )}

          {onClose && (
            <Button
              data-close-jid={chatJid || jid}
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-sidebar-accent"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {hasRoute && nextStop && (
        <div>
          <div className="border-base flex flex-col gap-2 border-b p-3">
            {/* Off-route information */}
            {isTracked && (
              <div
                className={cn(
                  "flex items-center gap-3",
                  isOffRoute ? "text-destructive" : "text-green-700",
                )}
              >
                {isOffRoute ? (
                  <>
                    <CircleAlert className="h-4 w-4" />
                    {t("fleet.fleetOverview.offRoute")}
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    {t("fleet.fleetOverview.onRoute")}
                  </>
                )}
              </div>
            )}
            {/* Distance/Task */}
            <div className="flex items-center gap-2">
              {getActionIcon(nextStop, lateIndicatorColor)}
              <div>
                <span className={`font-normal ${lateIndicatorColor}`}>
                  {distance}
                </span>
                <span className="ml-1 text-muted-foreground">
                  {getActionLabel(t, nextStop)}
                </span>
              </div>
            </div>
          </div>
          {(nextStop.estimated_arrival_time ||
            nextStop.scheduled_arrival_time) && (
            <div className="flex flex-col gap-2 p-3">
              {nextStop.scheduled_arrival_time && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-2">
                    {formatUserFriendlyDate(
                      new Date(nextStop.scheduled_arrival_time),
                    )}
                    <span className="text-muted-foreground">
                      {t("fleet.fleetOverview.scheduledArrival")}
                    </span>
                  </div>
                </div>
              )}

              {(nextStop.estimated_arrival_time_with_tacho ||
                nextStop.estimated_arrival_time) && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <CalendarCheck2
                      className={`h-4 w-4 text-muted-foreground ${lateIndicatorColor}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={lateIndicatorColor}>
                      {formatUserFriendlyDate(
                        new Date(
                          nextStop.estimated_arrival_time_with_tacho ||
                            nextStop.estimated_arrival_time!,
                        ),
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      {t("fleet.fleetOverview.estimatedArrival")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Separator */}
          {(nextStop.estimated_arrival_time ||
            nextStop.scheduled_arrival_time) && (
            <div className="border-t border-border"></div>
          )}
          {!!nextStop.remaining_time && (
            <div className="flex flex-col gap-2 p-3">
              {!!nextStop.remaining_time && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-2">
                    {formatDuration(getRemainingTime(nextStop))}
                    <span className="text-muted-foreground">
                      {t("fleet.fleetOverview.driving")}
                    </span>
                  </div>
                </div>
              )}

              {!!nextStop.remaining_time &&
                !!getRemainingTimeWithBreaks(nextStop) && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex items-center gap-2">
                      {formatDuration(
                        getRemainingTimeWithBreaks(nextStop) as number,
                      )}
                      <span className="text-muted-foreground">
                        {t("fleet.fleetOverview.withBreaks")}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Separator */}
          {!!nextStop.remaining_time && (
            <div className="border-t border-border"></div>
          )}

          {/* Destination */}
          <div className="border-base flex items-start gap-3 p-3">
            <div className="pt-1">{getActionIcon(nextStop)}</div>
            <div>
              {nextStop.location.companyName?.trim() && (
                <div className="font-semibold" data-testid="stop-company-name">
                  {nextStop.location.companyName.trim()}
                </div>
              )}
              <div className="font-normal">{getAddress(nextStop)}</div>
              <div className="text-sm text-muted-foreground">
                {getCoordinates(nextStop.location)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
